import React, { useState } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Menu as MenuIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Article as ArticleIcon,
  RocketLaunch as RocketIcon,
  OpenInNew as OpenInNewIcon,
  Shield as ShieldIcon,
  Email as EmailIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebase';

import { hasProjectWorkspaceAccess } from '../../utils/roles';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { themeMode, toggleTheme } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);

  const clientAccess = hasProjectWorkspaceAccess(currentUser);

  // Navigation Links
  const navLinks = [
    { label: 'Digital Onboarding', path: '/#onboarding', isHash: true },
    { label: 'AI Solutions', path: '/#ai-solutions', isHash: true },
    { label: 'How It Works', path: '/#how-it-works', isHash: true },
    { label: 'Packages', path: '/#packages', isHash: true },
    { label: 'Blog', path: '/blog', icon: <ArticleIcon fontSize="small" /> },
    { label: 'About', path: '/about' },
  ];

  const profileLinks = [];
  if (clientAccess) {
    profileLinks.push({ label: 'Project Workspace', path: '/project', icon: <RocketIcon /> });
  }
  profileLinks.push({ label: 'My Profile', path: '/profile', icon: <PersonIcon /> });
  profileLinks.push({ label: 'Settings', path: '/settings', icon: <SettingsIcon /> });

  // Handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleProfileMenuOpen = (event) => setProfileMenuAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchor(null);

  const handleNavigate = (path, isHash) => {
    handleMenuClose();
    handleProfileMenuClose();

    if (isHash) {
      const isHomeOrLaunchpad = location.pathname === '/' || location.pathname.includes('launchpad');
      if (!isHomeOrLaunchpad) {
        navigate(path);
      } else {
        const hash = path.replace('/#', '#');
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.location.hash = hash;
        }
      }
      return;
    }

    navigate(path);
  };

  const handleGetOnboarded = () => {
    handleMenuClose();
    handleProfileMenuClose();
    const isHomeOrLaunchpad = location.pathname === '/' || location.pathname.includes('launchpad');
    if (isHomeOrLaunchpad) {
      window.dispatchEvent(new CustomEvent('verveai:open-onboarding'));
      const element = document.getElementById('onboard') || document.getElementById('onboarding');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/launchpad/wizard');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
    handleProfileMenuClose();
  };

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        top: 0,
        zIndex: 1100,
        backdropFilter: 'blur(16px)',
        backgroundColor: themeMode === 'light' ? 'rgba(255, 255, 255, 0.94)' : 'rgba(11, 12, 14, 0.88)',
        borderBottom: themeMode === 'light' ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(247, 247, 248, 0.08)',
        boxShadow: themeMode === 'light' ? '0 1px 3px rgba(0, 0, 0, 0.05)' : 'none',
        transition: 'all 0.2s ease',
      }}
    >
      <Toolbar sx={{ maxWidth: '1280px', mx: 'auto', width: '100%', px: { xs: 2, sm: 3 } }}>
        {/* Brand Logo & Studio Logomark */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box
            component="a"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              cursor: 'pointer',
              gap: 1.5,
            }}
          >
            {/* Hexagon/Icon badge */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
                border: '1px solid rgba(6, 182, 212, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 15px rgba(6, 182, 212, 0.2)',
              }}
            >
              <RocketIcon sx={{ fontSize: 20, color: '#22d3ee' }} />
            </Box>

            {/* Brand text & Subtitle */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    letterSpacing: '-0.02em',
                    color: themeMode === 'light' ? '#0f172a' : '#f7f7f8',
                  }}
                >
                  VerveAI
                </Typography>
                <Box
                  component="span"
                  sx={{
                    fontSize: '9px',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    color: '#06b6d4',
                    bgcolor: 'rgba(6, 182, 212, 0.1)',
                    border: '1px solid rgba(6, 182, 212, 0.25)',
                    px: 0.8,
                    py: 0.1,
                    borderRadius: '4px',
                  }}
                >
                  LAUNCHPAD
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Desktop Navigation Links */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
          {navLinks.map((link) => (
            <Button
              key={link.label}
              onClick={() => handleNavigate(link.path, link.isHash)}
              sx={{
                color: themeMode === 'light' ? '#475569' : '#94a3b8',
                fontSize: '0.82rem',
                fontWeight: 500,
                px: 1.5,
                py: 0.75,
                borderRadius: '8px',
                '&:hover': {
                  color: themeMode === 'light' ? '#0f172a' : '#f7f7f8',
                  backgroundColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {link.icon && React.cloneElement(link.icon, { sx: { fontSize: 16 } })}
                <span>{link.label}</span>
                {link.badge && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '9px',
                      fontWeight: 800,
                      letterSpacing: '0.03em',
                      px: 0.8,
                      py: 0.2,
                      borderRadius: '4px',
                      bgcolor: 'rgba(16, 185, 129, 0.15)',
                      color: '#10b981',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                    }}
                  >
                    {link.badge}
                  </Box>
                )}
              </Box>
            </Button>
          ))}

          {/* Theme Mode Toggle (Dark / Light Switch) */}
          <Tooltip title={themeMode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
            <IconButton
              onClick={toggleTheme}
              size="small"
              sx={{
                ml: 0.5,
                color: themeMode === 'light' ? '#0f172a' : '#f7f7f8',
                bgcolor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                border: '1px solid',
                borderColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.12)',
                borderRadius: '8px',
                p: 0.8,
                '&:hover': {
                  bgcolor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {themeMode === 'light' ? (
                <DarkModeIcon sx={{ fontSize: 18, color: '#6366f1' }} />
              ) : (
                <LightModeIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
              )}
            </IconButton>
          </Tooltip>

          {/* CTA: Get Onboarded */}
          <Button
            onClick={handleGetOnboarded}
            variant="contained"
            sx={{
              ml: 1,
              background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
              color: '#ffffff',
              fontSize: '0.8rem',
              fontWeight: 600,
              px: 2,
              py: 0.75,
              borderRadius: '8px',
              boxShadow: '0 4px 14px rgba(6, 182, 212, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)',
                boxShadow: '0 6px 18px rgba(6, 182, 212, 0.35)',
              },
            }}
          >
            Get Onboarded
          </Button>

          {/* Auth State Button / Profile */}
          {!currentUser ? (
            <Button
              onClick={() => navigate('/login')}
              sx={{
                ml: 1,
                color: themeMode === 'light' ? '#475569' : '#94a3b8',
                fontSize: '0.78rem',
                fontWeight: 500,
                '&:hover': { color: themeMode === 'light' ? '#0f172a' : '#ffffff' },
              }}
            >
              Sign In
            </Button>
          ) : (
            <>
              <Tooltip title="User Account">
                <IconButton onClick={handleProfileMenuOpen} sx={{ ml: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: '#06b6d4',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                    }}
                    src={currentUser.photoURL}
                  >
                    {currentUser.displayName ? currentUser.displayName[0].toUpperCase() : <PersonIcon />}
                  </Avatar>
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    bgcolor: themeMode === 'light' ? '#ffffff' : '#131418',
                    border: themeMode === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(247, 247, 248, 0.1)',
                    minWidth: 160,
                  },
                }}
              >
                {profileLinks.map((link) => (
                  <MenuItem key={link.label} onClick={() => handleNavigate(link.path)}>
                    <ListItemIcon sx={{ minWidth: 30, color: themeMode === 'light' ? '#475569' : '#94a3b8' }}>
                      {React.cloneElement(link.icon, { fontSize: 'small' })}
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
                      {link.label}
                    </ListItemText>
                  </MenuItem>
                ))}
                <Divider sx={{ my: 1, borderColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)' }} />
                <MenuItem onClick={handleLogout} sx={{ color: '#ef4444' }}>
                  <ListItemIcon sx={{ minWidth: 30, color: '#ef4444' }}>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
                    Sign Out
                  </ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {/* Mobile Navigation Toggle */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
          {/* Mobile Theme Mode Toggle */}
          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{
              color: themeMode === 'light' ? '#0f172a' : '#f7f7f8',
              bgcolor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
              border: '1px solid',
              borderColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.12)',
              borderRadius: '6px',
              p: 0.6,
            }}
            title={themeMode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
          >
            {themeMode === 'light' ? (
              <DarkModeIcon sx={{ fontSize: 16, color: '#6366f1' }} />
            ) : (
              <LightModeIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
            )}
          </IconButton>

          <Button
            onClick={handleGetOnboarded}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
              color: '#ffffff',
              fontSize: '0.72rem',
              fontWeight: 600,
              px: 1.2,
              py: 0.4,
              borderRadius: '6px',
            }}
          >
            Get Onboarded
          </Button>

          <IconButton
            edge="end"
            onClick={handleMenuOpen}
            sx={{ color: themeMode === 'light' ? '#0f172a' : '#94a3b8' }}
            aria-label="Navigation Menu"
          >
            <MenuIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                bgcolor: themeMode === 'light' ? '#ffffff' : '#131418',
                border: themeMode === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(247, 247, 248, 0.1)',
                minWidth: 220,
                p: 1,
              },
            }}
          >
            {navLinks.map((link) => (
              <MenuItem
                key={link.label}
                onClick={() => handleNavigate(link.path, link.isHash)}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }}>
                  {link.label}
                </ListItemText>
                {link.badge && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      px: 0.8,
                      py: 0.2,
                      fontSize: '9px',
                      fontWeight: 800,
                      borderRadius: '4px',
                      bgcolor: 'rgba(16, 185, 129, 0.2)',
                      color: '#10b981',
                      border: '1px solid rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    {link.badge}
                  </Box>
                )}
              </MenuItem>
            ))}

            <Divider sx={{ my: 1, borderColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)' }} />

            {!currentUser ? (
              <MenuItem onClick={() => { handleMenuClose(); navigate('/login'); }}>
                <ListItemIcon sx={{ minWidth: 28, color: themeMode === 'light' ? '#475569' : '#94a3b8' }}>
                  <LoginIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
                  Sign In
                </ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={handleLogout} sx={{ color: '#ef4444' }}>
                <ListItemIcon sx={{ minWidth: 28, color: '#ef4444' }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
                  Sign Out
                </ListItemText>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
